// app/src/screens/AuthAction.jsx — the email-action landing page (D-AUTH §4). The
// verification email links here with ?mode=verifyEmail&oobCode=…&lang=…. The page applies
// the oobCode via the Firebase SDK, then — when a session exists — force-refreshes the ID
// token and pings /v1/me so the account stamps email_verified_at (and the signup grant
// lands) on THIS visit, then routes home (the Verify-Email gate now passes). Works signed-in
// or signed-out; the route is mounted in both trees so it survives the auth flip.
//
// Two robustness fixes over the first cut (both were live footguns):
//  · The oobCode PROMISE is deduped by code, so React StrictMode's double-mount (and any
//    remount across the auth flip) consumes it exactly once and BOTH mounts see the same
//    result. The old module-level Set let the 2nd (live) mount return early WITHOUT setting
//    state — which is why the page hung on "Verifying…" forever.
//  · Every await is timeout-guarded, so a stalled SDK/network call surfaces an error instead
//    of an endless spinner.

import React, { useEffect, useState } from 'react';
import { navigate } from '@common/router.js';
import { useI18n, LOCALE_TAGS } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useSeedLangCookie } from '../lib/locale-sync.jsx';
import { AuthFrame } from './SignIn.jsx';

// dedupe the apply by oobCode: both StrictMode mounts await the same promise -> single apply
const applying = new Map(); // oobCode -> Promise<'ok' | { err }>
function applyOnce(auth, code) {
  if (!applying.has(code)) applying.set(code, auth.applyActionCode(code).then(() => 'ok', (err) => ({ err })));
  return applying.get(code);
}
function withTimeout(p, ms) {
  return Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);
}
// an already-consumed / expired code is benign when the account ends up verified anyway
const BENIGN = new Set(['auth/invalid-action-code', 'auth/expired-action-code', 'auth/user-token-expired']);

export default function AuthAction() {
  const { t, setLocale } = useI18n();
  const { auth, authed, refreshMe, refreshCredits } = useSession();
  const [state, setState] = useState('working'); // working | done | error
  const [detail, setDetail] = useState(null);
  useSeedLangCookie();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const q = new URLSearchParams(window.location.search);
      const mode = q.get('mode');
      const oobCode = q.get('oobCode');
      const lang = q.get('lang');
      if (lang && LOCALE_TAGS.includes(lang)) setLocale(lang); // the email's language governs this page
      if (mode !== 'verifyEmail' || !oobCode) { setState('error'); setDetail(t('authAction.badLink')); return; }

      // 1) apply the code (deduped + timeout-guarded so it can never silently hang)
      let applyErr = null;
      try {
        const res = await withTimeout(applyOnce(auth, oobCode), 15000);
        if (res && res.err) applyErr = res.err;
      } catch (e) { applyErr = e; }

      // 2) pull the verified claim through so the account stamps + the grant lands NOW
      let verified = !applyErr;
      try {
        if (authed) {
          await withTimeout(auth.refreshToken(), 10000);
          const fresh = await withTimeout(refreshMe(), 10000);
          verified = !!(fresh && fresh.email_verified);
          if (verified) await refreshCredits().catch(() => {});
        }
      } catch { /* signed-out or transient — the stamp lands on next sign-in */ }

      if (cancelled) return;
      const benign = applyErr && BENIGN.has(applyErr.code);
      if (applyErr && !benign && !verified) { setState('error'); setDetail(applyErr.message); return; }
      setState('done');
      // authed -> go home and let the gate decide (it holds an unverified account on the
      // Verify-Email page, which self-heals via its poll — routing to /signin under authed
      // would land on a non-existent signed-in route). signed-out -> finish by signing in.
      setTimeout(() => { if (!cancelled) navigate(authed ? '/' : '/signin'); }, 1000);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthFrame title={t('authAction.title')} subtitle="">
      {state === 'working' && <p className="muted" style={{ margin: 0 }}>{t('authAction.working')}</p>}
      {state === 'done' && <p style={{ margin: 0 }}>{t('authAction.done')}</p>}
      {state === 'error' && (
        <>
          <p style={{ color: 'var(--coral)', fontSize: 13, margin: '0 0 14px' }}>{t('authAction.failed')}{detail ? ` — ${detail}` : ''}</p>
          <button className="q-btn press btn-ghost" style={{ width: '100%' }} onClick={() => navigate('/signin')}>{t('authAction.signIn')}</button>
        </>
      )}
    </AuthFrame>
  );
}
